import { JackpotMode, JackpotSmallSuitAnimationEvent } from '@amusnet/react-ui'
import {
    JackpotIndexSuitMap,
    JackpotLinkTypes,
    JackpotPhase,
    JackpotSuits,
    JackpotType,
    JackpotLinkStonesType,
    JackpotEQStatistics,
    JackpotCardsGameState,
    JackpotEQGameState,
    defaultJackpotEQStatistics,
} from 'game-ui-types'
import { create } from 'zustand'
import { useNavigationStore } from './navigationStore'

export interface JackpotStoreState {
    jackpotStatsAnimationDurationSeconds: number
    isJackpotSettingActive: boolean
    setIsJackpotSettingActive: (isActive: boolean) => void
    jackpotType: JackpotType
    setJackpotType: (type: JackpotType) => void
    jackpotGameState: JackpotCardsGameState | JackpotEQGameState
    isLinkJackpotType: boolean
    isEQJackpotType: boolean
    setJackpotGameState: (state: JackpotCardsGameState | JackpotEQGameState) => void
    jackpotPhase: JackpotPhase
    isJackpotStarted: boolean
    isJackpotRoundRunning: boolean
    setJackpotPhase: (jackpotPhase: JackpotPhase) => void
    isJackpotLinkHoldSpin: boolean
    setIsJackpotLinkHoldSpin: (isJackpotLinkHoldSpin: boolean) => void
    isJackpotEQStarted: boolean
    setIsJackpotEQStarted: (isJackpotEQStarted: boolean) => void
    toolTipDateSeparator: string
    setToolTipDateSeparator: (separator: string) => void
    currencyHideCentsOnlyInJackpotBoxes: boolean
    setCurrencyHideCentsOnlyInJackpotBoxes: (hide: boolean) => void
    jackpotEQStatistics: JackpotEQStatistics
    setJackpotEQStatistics: (stats: JackpotEQStatistics) => void
    pickedSuits: { [key in JackpotSuits]: number }
    resetPickedSuits: () => void
    pickedGems: { [key in JackpotLinkStonesType]: number }
    setPickedGems: (data: { [key in JackpotLinkStonesType]: number }) => void
    freezeIndex: number | -1
    setFreezeIndex: (index: number | -1) => void
    jackpotStatsCountdownToZeroDurationSeconds: number
    setJackpotStatsCountdownToZeroDurationSeconds: (duration: number) => void
    hasLeadElement: boolean
    setHasLeadElement: (hasElement: boolean) => void
    latestSuitEvent: JackpotSmallSuitAnimationEvent | null
    setLatestSuitEvent: (event: JackpotSmallSuitAnimationEvent | null) => void
    mode: JackpotMode
    setMode: (mode: JackpotMode) => void
    winningLevelIndex?: number
    setWinningLevelIndex: (index: number | undefined) => void
    isHistoryEnabled: boolean
    setIsHistoryEnabled: (isHistoryEnabled: boolean) => void
    closeHistorySignal: number
    closeJackpotHistory: () => void
}

export const useJackpotStore = create<JackpotStoreState>((set) => ({
    jackpotStatsAnimationDurationSeconds: 5,

    isJackpotSettingActive: false,
    setIsJackpotSettingActive: (isActive: boolean) => {
        set({ isJackpotSettingActive: isActive })
    },

    isLinkJackpotType: false,
    isEQJackpotType: false,

    jackpotType: JackpotType.JACKPOT_CARDS,
    setJackpotType: (jackpotType: JackpotType) => {
        set({
            jackpotType,
            isLinkJackpotType: jackpotType === JackpotType.JACKPOT_LINK,
            isEQJackpotType: jackpotType === JackpotType.JACKPOT_EGYPT_QUEST,
        })
    },
    jackpotGameState: {},
    setJackpotGameState: (state: JackpotCardsGameState | JackpotEQGameState) => {
        // Suits in Remote jackpot returns 0, 1, 2, 3. Local jackpot returns 11, 24, 37, 50
        // Should receive in this format: 0, 1, 2, 3
        const allPickedSuits = Object.values(state).map((n) => JackpotIndexSuitMap[n])
        const pickedSuitsMap = allPickedSuits.reduce(
            (acc, current) => {
                acc[current] = acc[current] + 1

                return acc
            },
            {
                [JackpotSuits.CLUBS]: 0,
                [JackpotSuits.DIAMONDS]: 0,
                [JackpotSuits.HEARTS]: 0,
                [JackpotSuits.SPADES]: 0,
            }
        )

        set({ jackpotGameState: state, pickedSuits: pickedSuitsMap })
    },

    jackpotPhase: JackpotPhase.CLOSED,
    isJackpotStarted: false,
    isJackpotRoundRunning: false,
    setJackpotPhase: (jackpotPhase: JackpotPhase) => {
        set({
            jackpotPhase,
            isJackpotStarted: jackpotPhase !== JackpotPhase.CLOSED && jackpotPhase !== JackpotPhase.PENDING,
            isJackpotRoundRunning: jackpotPhase === JackpotPhase.RUNNING,
        })
    },

    isJackpotLinkHoldSpin: false,
    setIsJackpotLinkHoldSpin: (isJackpotLinkHoldSpin: boolean) => {
        set({ isJackpotLinkHoldSpin })
    },

    isJackpotEQStarted: false,
    setIsJackpotEQStarted: (isJackpotEQStarted: boolean) => {
        set({ isJackpotEQStarted })
    },

    toolTipDateSeparator: '',
    setToolTipDateSeparator: (separator: string) => {
        set({ toolTipDateSeparator: separator })
    },

    currencyHideCentsOnlyInJackpotBoxes: false,
    setCurrencyHideCentsOnlyInJackpotBoxes: (hide: boolean) => {
        set({ currencyHideCentsOnlyInJackpotBoxes: hide })
    },

    jackpotEQStatistics: defaultJackpotEQStatistics,
    setJackpotEQStatistics: (stats: JackpotEQStatistics) => {
        set((state) => ({
            jackpotEQStatistics: {
                ...state.jackpotEQStatistics,
                // these props are not always sent, reset them otherwise it looks like someone is winning nonstop.
                winLevel: -1,
                winAmount: -1,
                screenName: '',
                ...stats,
            },
        }))
    },

    pickedSuits: {
        [JackpotSuits.CLUBS]: 0,
        [JackpotSuits.DIAMONDS]: 0,
        [JackpotSuits.HEARTS]: 0,
        [JackpotSuits.SPADES]: 0,
    },
    resetPickedSuits: () => {
        set({
            pickedSuits: {
                [JackpotSuits.CLUBS]: 0,
                [JackpotSuits.DIAMONDS]: 0,
                [JackpotSuits.HEARTS]: 0,
                [JackpotSuits.SPADES]: 0,
            },
        })
    },

    pickedGems: {
        [JackpotLinkTypes.GRAND]: 0,
        [JackpotLinkTypes.MAJOR]: 0,
    },
    setPickedGems: (pickedGems) => {
        set({ pickedGems })
    },

    freezeIndex: -1,
    setFreezeIndex: (index: number | -1) => {
        // When index populated the relative jackpot stats item has been won by the current player
        // so that during jackpot results the statistics can animate the win
        // Set to -1 to reset
        // Index is JackpotIndexSuitMap or JackpotLinkIndexTypeMap
        set({ freezeIndex: index })
    },

    jackpotStatsCountdownToZeroDurationSeconds: 4,
    setJackpotStatsCountdownToZeroDurationSeconds: (duration: number) => {
        set({ jackpotStatsCountdownToZeroDurationSeconds: duration })
    },

    hasLeadElement: false,
    setHasLeadElement: (hasElement = true) => {
        set({ hasLeadElement: hasElement })
    },
    latestSuitEvent: null,
    setLatestSuitEvent: (event: JackpotSmallSuitAnimationEvent | null) => {
        set({ latestSuitEvent: event })
    },
    mode: JackpotMode.IDLE,
    setMode: (mode: JackpotMode) => {
        set({ mode })
    },
    winningLevelIndex: undefined,
    setWinningLevelIndex: (index: number | undefined) => {
        set({ winningLevelIndex: index })
    },

    isHistoryEnabled: true,
    setIsHistoryEnabled: (isHistoryEnabled: boolean) => {
        set({ isHistoryEnabled })
    },

    closeHistorySignal: 0,
    closeJackpotHistory: () => {
        set((state) => ({ closeHistorySignal: state.closeHistorySignal + 1 }))
    },
}))

// Jackpot history targets same behavior as settings menu in terms of disabling/enabling
useNavigationStore.subscribe((state, prevState) => {
    if (state.isMenuDisabled !== prevState.isMenuDisabled) {
        useJackpotStore.getState().setIsHistoryEnabled(!state.isMenuDisabled)
    }
})
